import React, { useEffect, useState } from 'react'
import { graphql } from 'gatsby'
import { mergePrismicPreviewData } from 'gatsby-source-prismic'
import scrollIntoView from 'smooth-scroll-into-view-if-needed'
import { Section, Speakers, ButtonWithModal, SEO, ScrollWebinarToView } from 'newComponents'
import Layout from '../components/layout.js'
import * as Slices from '../slices'
import starsBg from '../images/webinars/stars_bg.jpg'
import './webinar.scss'

const IS_BROWSER = typeof window !== 'undefined'

export const Slice = ({ data, location }) => {
  const { slice_type: sliceType } = data
  const allSlices = { ...Slices }
  // Convert slice type from snake_case to TitleCase
  if (!sliceType) return null
  const sliceName = sliceType
    .split('_')
    .map((item) => item.charAt(0).toUpperCase() + item.substring(1))
    .join('')

  const CustomSlice = allSlices[sliceName]
  return <CustomSlice data={data} location={location} />
}

const Webinar = ({ data: staticData, location }) => {
  const previewData = IS_BROWSER && window.__PRISMIC_PREVIEW_DATA__
  const data = mergePrismicPreviewData({ staticData, previewData })

  const { webinar } = data
  const { data: pageData, uid } = webinar

  const {
    body: sliceData,
    metaTitle,
    metaDesc,
    ogImage,
    // recording_length: length,
    subtitle,
    title,
    category,
    speakers,
    form_id: formId,
    // hidden,
    date,
    time,
    // description
    is_webinar_series: isWebinarSeries
  } = pageData

  // seo data
  const seo = {
    title: metaTitle?.text,
    desc: metaDesc?.text,
    banner: ogImage?.localFile?.childImageSharp?.fixed?.src
  }

  // state
  const [isUpcoming, setIsUpcoming] = useState(null)

  // content checks
  const titleHtml = title?.html
  const subtitleHtml = subtitle?.html
  const WebinarDate = new Date(date)

  useEffect(() => {
    setIsUpcoming(WebinarDate > Date.now())
  }, [])

  const RenderWebinarContent = () => {
    if (isWebinarSeries) {
      return (
        <div className="webinar-series">
          {sliceData.map((slice) => (
            <Slice key={slice.id} data={{ ...slice, uid }} location={location} />
          ))}
        </div>
      )
    }
    return (
      <div className="webinar">
        <Section bgImage={starsBg} className="webinar-header">
          {category && <div className="webinar-category-text">{`WEBINAR | ${category}`}</div>}
          {titleHtml && <div className="webinar-title" dangerouslySetInnerHTML={{ __html: titleHtml }} />}
          {subtitleHtml && <div className="webinar-subtitle" dangerouslySetInnerHTML={{ __html: subtitleHtml }} />}
          {isUpcoming && (
            <p className="webinar-datetext">
              {date} {time?.text ? `| ${time.text}` : ''}
            </p>
          )}
          <div className="webinar-cta homepage-hero-ctas">
            {isUpcoming !== null && (
              <>
                {isUpcoming ? (
                  <ButtonWithModal
                    buttonText="REGISTER"
                    modalTitle={title.text}
                    modalSubtitle={subtitle.text}
                    formId={formId}
                  />
                ) : (
                  <ButtonWithModal
                    buttonText="GET THE RECORDING"
                    modalTitle={title.text}
                    modalSubtitle={subtitle.text}
                    formId={formId}
                  />
                )}
              </>
            )}
          </div>
        </Section>
        <div className="webinar-body">
          <div className="webinar-slice-content">
            {sliceData.map((slice) => (
              <Slice key={slice.id} data={{ ...slice, uid }} location={location} />
            ))}
          </div>
          <Speakers speakers={speakers} />
        </div>
      </div>
    )
  }

  return (
    <Layout>
      <SEO {...seo} />
      <RenderWebinarContent />
      <ScrollWebinarToView />
    </Layout>
  )
}

export default Webinar

export const webinarQuery = graphql`
  query WebinarBySlug($uid: String!) {
    webinar: prismicWebinar(uid: { eq: $uid }) {
      id
      uid
      prismicId
      data {
        metaTitle: meta_title {
          text
        }
        metaDesc: meta_description {
          text
        }
        ogImage: open_graph_image {
          url
          localFile {
            childImageSharp {
              fixed(width: 1200, height: 680, quality: 90) {
                src
              }
            }
          }
        }
        category
        date(formatString: "ddd, MMMM D, YYYY")
        description {
          html
          text
        }
        form_id
        hidden
        is_webinar_series
        recording_length
        speakers {
          avatar {
            url
            localFile {
              childImageSharp {
                fluid(maxWidth: 1200, quality: 90) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
          }
          logo {
            url
            localFile {
              childImageSharp {
                fluid(maxWidth: 1200, quality: 90) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
          }
          name {
            text
            html
          }
          role {
            html
            text
          }
        }
        subtitle {
          text
          html
        }
        time {
          html
          text
        }
        title {
          html
          text
        }
        body {
          ...WebinarWysiwyg
          ...WebinarSeriesAgenda
          ...WebinarWebinarHeads
          ...WebinarWebinarSeriesItems
        }
      }
    }
  }
`
